<script setup>
/**
 * HelpPopover: the one ⓘ in the send modal's header and "How sending works"
 * (2026-10-22, Chris; design `gift-flow-review-2026-10-22/DESIGN.md` "The
 * pattern", mockup S5-help-desktop). After the first two opens every
 * instruction lives here and nowhere else. Closes on ⓘ again, a click outside
 * or Escape.
 */
import { ref, onMounted, onBeforeUnmount } from 'vue'

defineProps({
  // [{ key, icon, text }] from HELP_LINES
  lines: { type: Array, required: true },
})
const open = ref(false)
const root = ref(null)
function toggle () { open.value = !open.value }
function onDoc (e) {
  if (open.value && root.value && !root.value.contains(e.target)) open.value = false
}
function onKey (e) {
  if (open.value && e.key === 'Escape') { e.stopImmediatePropagation(); open.value = false }
}
onMounted(() => {
  document.addEventListener('mousedown', onDoc, true)
  document.addEventListener('keydown', onKey, true)
})
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDoc, true)
  document.removeEventListener('keydown', onKey, true)
})
defineExpose({ open })
</script>

<template>
  <div ref="root" class="hp">
    <button type="button" class="hp-i" :class="{ on: open }" aria-label="How sending works" :aria-expanded="open ? 'true' : 'false'"
            aria-controls="hp-pop" data-testid="send-help" @click="toggle">i</button>
    <div v-if="open" id="hp-pop" class="hp-pop" role="dialog" aria-label="How sending works" data-testid="send-help-pop">
      <p class="hp-title">How sending works</p>
      <ul class="hp-list">
        <li v-for="l in lines" :key="l.key" class="hp-line">
          <i :class="['fas', l.icon]" aria-hidden="true"></i><span>{{ l.text }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.hp { position: relative; flex: none;
  --ui: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; --display: 'Letter Display', 'Cinzel', Georgia, serif; }
.hp-i { width: 44px; height: 44px; border-radius: 50%; border: 1.5px solid #ddd0ef; background: #fff; cursor: pointer;
  display: grid; place-items: center; color: #4e2776; font: 700 18px/1 Georgia, serif; }
.hp-i:hover { border-color: #b9a2dc; }
.hp-i.on { background: #4e2776; border-color: #4e2776; color: #fff; }
.hp-i:focus-visible { outline: 2px solid #7b4bb0; outline-offset: 3px; }
.hp-pop { position: absolute; right: -8px; top: calc(100% + 10px); z-index: 60; width: min(380px, calc(100vw - 32px)); box-sizing: border-box;
  padding: 16px 18px 6px; border-radius: 18px; background: #fff; border: 1px solid #e3d4f5;
  box-shadow: 0 24px 48px -20px rgba(42, 24, 69, 0.55); text-align: left; }
.hp-pop::before { content: ''; position: absolute; top: -7px; right: 24px; width: 12px; height: 12px; background: #fff;
  border-left: 1px solid #e3d4f5; border-top: 1px solid #e3d4f5; transform: rotate(45deg); }
.hp-title { margin: 0 0 6px; font: 600 11px var(--display); letter-spacing: 0.2em; text-transform: uppercase; color: #8a6a2a; }
.hp-list { list-style: none; margin: 0; padding: 0; }
.hp-line { display: flex; gap: 12px; padding: 12px 0; border-top: 1px solid #f0e8f8; font: 500 14px/1.5 var(--ui); color: #2a1845; }
.hp-line:first-child { border-top: 0; }
.hp-line i { flex: none; width: 16px; margin-top: 3px; color: #7b4bb0; font-size: 14px; text-align: center; }
@media (max-width: 560px) {
  .hp-pop { right: -56px; }
  .hp-pop::before { right: 72px; }
}
</style>
